import React, { useState, ReactNode } from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { AppBar, IconButton, Toolbar } from '@mui/material/';
import MenuIcon from '@mui/icons-material/Menu';
import * as css from './toolbar.module.scss';
import Menu from './menu';
import { useIntl } from 'react-intl';
import translations from './toolbar.i18n';
import Version from '../components/version/version';
import useContextNotUndefined from '../hooks/useContextNotUndefined';

type ActionItemsContextProps = {
  setActionItems: (actionItems: ReactNode) => void;
};

export const SetActionItemsContext = React.createContext<
  ActionItemsContextProps | undefined
>(undefined);

export const useActionItemsContext = () =>
  useContextNotUndefined(SetActionItemsContext, 'ActionItemsContext');

interface Props {
  title: string;
  subtitle?: string;
  menuItems?: ReactNode;
  actionItems?: ReactNode;
  titleActions?: ReactNode;
  permanentMenu?: boolean;
}

const TopToolbar: React.FC<React.PropsWithChildren<Props>> = ({
  title,
  subtitle,
  menuItems,
  actionItems,
  titleActions,
  permanentMenu,
}) => {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('md'));
  const [menuopen, setMenuopen] = useState(false);
  const onClose = () => setMenuopen(!menuopen);

  const handleHamburgerIconClick = () => {
    onClose();
  };

  const { formatMessage } = useIntl();
  const iconButtonLabel: string = formatMessage({
    id: translations.iconButtonLabel.id,
  });

  return (
    <>
      {permanentMenu ? (
        <Menu children={menuItems} permanent />
      ) : (
        <Menu menuOpen={menuopen} children={menuItems} onClose={onClose} />
      )}
      <AppBar
        className={permanentMenu ? css.permanentMenuToolbar : css.topToolbar}
        position={mobile ? 'static' : 'fixed'}
        elevation={0}
        color={'inherit'}
      >
        <Toolbar>
          <div className={css.flex}>
            <div className={css.flexRow}>
              {!permanentMenu && (
                <IconButton
                  color="inherit"
                  aria-label={iconButtonLabel}
                  edge="start"
                  onClick={handleHamburgerIconClick}
                  size="large"
                >
                  <MenuIcon />
                </IconButton>
              )}
              <div className={css.title}>
                <div className={css.titleContainer}>
                  <h1>{title}</h1>
                  {titleActions && (
                    <div className={css.titleActions}>{titleActions}</div>
                  )}
                </div>
                {subtitle && <p className={css.titleDescription}>{subtitle}</p>}
              </div>
            </div>
            <div className={css.flexRow}>
              <div className={css.actions}>{actionItems}</div>
              {!permanentMenu && <Version onlyEnv />}
            </div>
          </div>
        </Toolbar>
      </AppBar>
    </>
  );
};

export default TopToolbar;
